
/**
 * ErrorBoundary — catches unhandled errors in child components and
 * displays a recovery UI instead of crashing the entire app.
 */

import React from 'react';
import { reportClientDiagnostic } from '../../services/clientDiagnostics';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /**
   * When this value changes, a caught error is cleared automatically.
   *
   * Pass the route path from a route-scoped boundary: without it, a crash in one
   * view latches the boundary open, and navigating elsewhere keeps showing the
   * error screen for a view the user has already left. With it, moving to
   * another route recovers on its own.
   */
  resetKey?: string | number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    void reportClientDiagnostic(
      'react_error_boundary',
      {
        componentStack: errorInfo.componentStack?.slice(0, 1200),
      },
      error.message,
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.handleReset();
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            padding: '2rem',
            maxWidth: 600,
            margin: '4rem auto',
            fontFamily: 'system-ui, sans-serif',
            color: '#e5e7eb',
            background: '#1f2937',
            borderRadius: '0.75rem',
            border: '1px solid #374151',
          }}
        >
          <h2 style={{ color: '#f87171', marginTop: 0 }}>Something went wrong</h2>
          <p style={{ color: '#9ca3af' }}>
            An unexpected error occurred. You can try recovering or reload the page.
          </p>
          {this.state.error && (
            <pre
              style={{
                background: '#111827',
                padding: '1rem',
                borderRadius: '0.5rem',
                fontSize: '0.8rem',
                overflowX: 'auto',
                color: '#fca5a5',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                padding: '0.5rem 1.25rem',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1.25rem',
                background: '#374151',
                color: '#e5e7eb',
                border: '1px solid #4b5563',
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
